import { useState, useMemo, type FormEvent } from "react";
import { Box, Button, TextField, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { authenticateGallery } from "@/api/galleriesApi";

/** Sanitize URL for CSS url() injection safety. */
function safeCssUrl(url: string | null | undefined): string | null {
  if (!url) return null;
  if (/[)"';{}]/.test(url)) return null;
  return url;
}

interface GalleryPasswordGateProps {
  galleryName: string;
  logoUrl?: string | null;
  backgroundUrl?: string | null;
  accentColor?: string;
  onAuthenticated: () => void;
  slug: string;
}

export default function GalleryPasswordGate({
  galleryName,
  logoUrl,
  backgroundUrl,
  accentColor,
  onAuthenticated,
  slug,
}: GalleryPasswordGateProps) {
  const { t } = useTranslation("gallery");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const safeBackgroundUrl = useMemo(() => safeCssUrl(backgroundUrl), [backgroundUrl]);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      const res = await authenticateGallery(slug, password);

      if (res.ok) {
        onAuthenticated();
      } else if (res.status === 429) {
        setError(t("password.tooManyAttempts"));
      } else {
        setError(t("password.incorrect"));
      }
    } catch {
      setError(t("password.connectionError"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        bgcolor: "#000",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        position: "relative",
      }}
    >
      {/* Background image + overlay */}
      {safeBackgroundUrl && (
        <>
          <Box
            sx={{
              position: "fixed",
              inset: 0,
              backgroundImage: `url(${safeBackgroundUrl})`,
              backgroundSize: "cover",
              backgroundPosition: "center",
              backgroundRepeat: "no-repeat",
              zIndex: 0,
            }}
          />
          <Box
            sx={{
              position: "fixed",
              inset: 0,
              bgcolor: "rgba(0, 0, 0, 0.75)",
              zIndex: 0,
            }}
          />
        </>
      )}

      <Box
        component="form"
        onSubmit={handleSubmit}
        sx={{
          position: "relative",
          zIndex: 1,
          bgcolor: "rgba(255,255,255,0.05)",
          borderRadius: 2,
          p: 4,
          maxWidth: 400,
          width: "100%",
          mx: 2,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 3,
          backdropFilter: safeBackgroundUrl ? "blur(12px)" : undefined,
        }}
      >
        {logoUrl && (
          <Box
            component="img"
            loading="lazy"
            src={logoUrl}
            alt={galleryName}
            sx={{ maxHeight: 80, maxWidth: 200, objectFit: "contain" }}
          />
        )}
        <Typography variant="h6" sx={{ color: "#fff", textAlign: "center" }}>
          {galleryName}
        </Typography>
        <TextField
          type="password"
          label={t("password.label")}
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          error={!!error}
          helperText={error}
          fullWidth
          autoFocus
          sx={{
            "& .MuiOutlinedInput-root": {
              color: "#fff",
              "& fieldset": { borderColor: "rgba(255,255,255,0.3)" },
              "&:hover fieldset": { borderColor: "rgba(255,255,255,0.5)" },
            },
            "& .MuiInputLabel-root": { color: "rgba(255,255,255,0.7)" },
          }}
        />
        <Button
          type="submit"
          variant="contained"
          fullWidth
          disabled={!password || loading}
          sx={accentColor ? {
            bgcolor: accentColor,
            "&:hover": { bgcolor: accentColor, filter: "brightness(0.85)" },
          } : undefined}
        >
          {loading ? t("password.verifying") : t("password.access")}
        </Button>
      </Box>
    </Box>
  );
}
